import { Fragment } from 'react';
import type { MsrVdiFunktionsliste, MsrVdiSpalte } from './typen';
import { z } from './gemeinsam';
import { EmptyState } from '../../muster';

const ZEILEN_JE_BLATT = 25;

/** Fasst gleiche, aufeinanderfolgende Schlüssel zu einer Gruppe (colSpan) zusammen. */
function gruppieren<T>(liste: T[], schluessel: (t: T) => string) {
  const gruppen: { wert: string; anzahl: number; start: number }[] = [];
  liste.forEach((t, i) => {
    const wert = schluessel(t);
    const letzte = gruppen[gruppen.length - 1];
    if (letzte && letzte.wert === wert) letzte.anzahl += 1;
    else gruppen.push({ wert, anzahl: 1, start: i });
  });
  return gruppen;
}

/** Die laufende Nummer einer Spalte innerhalb ihres Abschnitts (1-basiert, wie im Druckbild). */
function spaltenNummer(spalten: MsrVdiSpalte[], index: number): number {
  const abschnitt = spalten[index].abschnitt ?? spalten[index].gruppe;
  let n = 0;
  for (let i = 0; i <= index; i += 1) {
    if ((spalten[i].abschnitt ?? spalten[i].gruppe) === abschnitt) n += 1;
  }
  return n;
}

/**
 * Die GA-Funktionsliste VDI 3814 Blatt 4.3 (Vorgabe Teuber + Viel) — bildet
 * das Druckbild `unterlagen/vdi-vorgabe/DP-Liste-Vorlage.pdf` nach: fünf
 * Kopfzeilen (Gruppe → Untergruppe → gedrehter Einzeltitel → Abschnitt →
 * Spalte), links und rechts „Zeile Nr.", dazwischen die Datenpunktspalte
 * „BAS/Klartext" und 56 schmale Zahlenspalten, zeilenweise hell hinterlegt,
 * alle 25 Zeilen eine Blattmarke, unten „Summe Funktionen". Kopf- und
 * Fußblock (Planersteller, Projekt, ASP, Anlage, Protokoll, Planstand,
 * Datei) liegen außerhalb der Tabelle — im Druckbild bilden sie dort die
 * linke obere Ecke bzw. die Fußzeile der Vorlage.
 */
export function FunktionslisteVdiTabelle({ f }: { f: MsrVdiFunktionsliste }) {
  const gruppen = gruppieren(f.spalten, (s) => s.gruppe);
  const untergruppen = gruppieren(f.spalten, (s) => s.untergruppe);
  const abschnitte = gruppieren(f.spalten, (s) => s.abschnitt ?? s.gruppe);

  const spaltenGesamt = 3 + f.spalten.length + 1;

  return (
    <div>
      <div className="feldgitter" style={{ marginBottom: 10 }}>
        <div><div className="feldname">GA-Funktionsliste</div><div>VDI 3814 Blatt 4.3</div></div>
        <div><div className="feldname">Gewerk</div><div>{f.kopf.gewerk}</div></div>
        <div><div className="feldname">Anlage(-n)</div><div>{f.kopf.anlage} — {f.kopf.anlageBezeichnung}</div></div>
      </div>

      <div className="tabellenrahmen">
        <table className="zuordnung tabelle-vdi43">
          <thead>
            <tr>
              <th rowSpan={5}>Zeile Nr.</th>
              <th rowSpan={5}>Datenpunkt mit Klartext und Benutzeradresse (BAS)</th>
              {gruppen.map((g) => <th key={g.start} colSpan={g.anzahl}>{g.wert}</th>)}
              <th rowSpan={5}>Bemerkungen und Referenzierungen</th>
              <th rowSpan={5}>Zeile Nr.</th>
            </tr>
            <tr>
              {untergruppen.map((g) => <th key={g.start} colSpan={g.anzahl}>{g.wert}</th>)}
            </tr>
            <tr>
              {f.spalten.map((s) => (
                <th key={s.schluessel} className="spalte-gedreht vdi-spalte" title={s.titel}
                  style={{ height: 150 }}>
                  {s.titel}
                </th>
              ))}
            </tr>
            <tr>
              {abschnitte.map((g) => <th key={g.start} colSpan={g.anzahl}>{g.wert}</th>)}
            </tr>
            <tr>
              {f.spalten.map((s, i) => (
                <th key={s.schluessel} className="vdi-spalte">{spaltenNummer(f.spalten, i)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {f.zeilen.map((zeile, i) => {
              const blattEnde = (i + 1) % ZEILEN_JE_BLATT === 0 && i + 1 < f.zeilen.length;
              return (
                <Fragment key={zeile.nr}>
                  <tr className={zeile.nr % 2 === 1 ? 'vdi-zeile-hell' : undefined}>
                    <td style={{ textAlign: 'center' }}>{zeile.nr}</td>
                    <td className="vdi-bas-zelle">
                      <span className="vdi-bas">
                        {zeile.bas || <em className="schwach">— keine BAS —</em>}
                      </span>
                      <span className="vdi-klartext">{zeile.klartext}</span>
                    </td>
                    {f.spalten.map((s) => (
                      <td key={s.schluessel} className="vdi-spalte">
                        {zeile.werte[s.schluessel] ? z(zeile.werte[s.schluessel]) : ''}
                      </td>
                    ))}
                    <td style={{ fontSize: 11 }}>{zeile.bemerkung ?? ''}</td>
                    <td style={{ textAlign: 'center' }}>{zeile.nr}</td>
                  </tr>
                  {blattEnde && (
                    <tr className="vdi-blattmarke">
                      <td colSpan={spaltenGesamt}>
                        Blatt {(i + 1) / ZEILEN_JE_BLATT} von {f.seiten}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {f.zeilen.length === 0 && (
              <tr><td colSpan={spaltenGesamt}>
                <EmptyState klein titel="Für diese Anlage steht kein Datenpunkt in der Funktionsliste."
                  erklaerung="Die Zeilen entstehen aus den Datenpunkten der Anlage im Ableitungslauf." />
              </td></tr>
            )}
          </tbody>
          {f.zeilen.length > 0 && (
            <tfoot>
              <tr className="zeile-summe">
                <td colSpan={2}>Summe Funktionen</td>
                {f.spalten.map((s) => (
                  <td key={s.schluessel} className="vdi-spalte">
                    {f.summen[s.schluessel] ? z(f.summen[s.schluessel]) : ''}
                  </td>
                ))}
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="feldgitter" style={{ marginTop: 10 }}>
        <div><div className="feldname">Planersteller</div><div>{f.kopf.planersteller}</div></div>
        <div><div className="feldname">Projekt</div><div>{f.kopf.projektnummer} — {f.kopf.projekt}</div></div>
        <div><div className="feldname">ASP</div><div>{f.kopf.aspNummer} — {f.kopf.asp}</div></div>
        <div><div className="feldname">Anlage</div><div>{f.kopf.anlage} — {f.kopf.anlageBezeichnung}</div></div>
        <div>
          <div className="feldname">Datenkommunikationsprotokoll AE ASP</div>
          <div>{f.kopf.protokoll}</div>
        </div>
        <div><div className="feldname">Planstand</div><div>{f.kopf.planstand}</div></div>
        <div><div className="feldname">Datei</div><div>{f.kopf.datei}</div></div>
      </div>
    </div>
  );
}
